import React, { useState, useEffect } from 'react';
import { Calendar, CheckSquare, RefreshCw, Loader2, X, Lock, Database, Hash, Mail } from 'lucide-react';
import { THEME } from '../theme';
import { useOAuthIntegrations } from '../hooks/useOAuthIntegrations';

const API_BASE_URL = (process.env.REACT_APP_API_URL || "").replace("http://", "https://");

const Integrations = ({ darkMode, user }) => {
  // Use custom OAuth hook
  const { 
    integrations, 
    loading, 
    error: oauthError, 
    connect, 
    disconnect: disconnectIntegration, 
    refresh,
    fetchStatus 
  } = useOAuthIntegrations();
  
  const [statusMessage, setStatusMessage] = useState(null);
  const [manageModal, setManageModal] = useState(null); // { provider: 'notion', data: {...} }
  const [managementData, setManagementData] = useState({ pages: [], channels: [], scopes: [] });
  const [loadingManagement, setLoadingManagement] = useState(false);
  
  // Normalize status for backward compatibility (google_calendar <-> googleCalendar)
  const status = {
    notion: integrations.notion || { connected: false },
    slack: integrations.slack || { connected: false },
    googleCalendar: integrations.google_calendar || integrations.googleCalendar || { connected: false },
    google: integrations.google || { connected: false }
  };

  useEffect(() => {
    // Check for OAuth callback in URL
    const params = new URLSearchParams(window.location.search);
    const oauthStatus = params.get('status');
    const provider = params.get('provider');
    const message = params.get('message');

    if (oauthStatus === 'success') {
      setStatusMessage({ type: 'success', text: `${provider} connected successfully!` });
      setTimeout(() => setStatusMessage(null), 5000);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
      // Refresh status
      fetchStatus();
    } else if (oauthStatus === 'error') {
      const errorMsg = message || 'Authorization failed';
      setStatusMessage({ type: 'error', text: `Failed to connect ${provider}: ${errorMsg}` });
      setTimeout(() => setStatusMessage(null), 8000);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (oauthStatus === 'denied') {
      setStatusMessage({ 
        type: 'error', 
        text: `You denied access to ${provider}. Please try again if you want to connect.` 
      });
      setTimeout(() => setStatusMessage(null), 8000);
      // Clean URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [fetchStatus]);

  // Display OAuth errors from the hook
  useEffect(() => {
    if (oauthError) {
      setStatusMessage({ type: 'error', text: oauthError });
      setTimeout(() => setStatusMessage(null), 5000);
    }
  }, [oauthError]);

  // --- CONNECTION HANDLERS ---

  const handleGoogleConnect = async () => {
    try {
      if (!user) {
        setStatusMessage({ type: 'error', text: 'You must be logged in to connect.' });
        return;
      }
      await connect('google');
    } catch (error) {
      // Error is already set in the hook
      console.error('Failed to connect Google:', error);
    }
  };

  const handleGoogleCalendarConnect = async () => {
    try {
      if (!user) {
        setStatusMessage({ type: 'error', text: 'You must be logged in to connect.' });
        return;
      }
      // Use google_calendar as the provider ID (per API docs)
      await connect('google_calendar');
    } catch (error) {
      // Error is already set in the hook
      console.error('Failed to connect Google Calendar:', error);
    }
  };

  const handleSlackConnect = async () => {
    try {
      if (!user) {
        setStatusMessage({ type: 'error', text: 'You must be logged in to connect.' });
        return;
      }
      await connect('slack');
    } catch (error) {
      // Error is already set in the hook
      console.error('Failed to connect Slack:', error);
    }
  };

  const handleNotionConnect = async () => {
    try {
      if (!user) {
        setStatusMessage({ type: 'error', text: 'You must be logged in to connect.' });
        return;
      }
      await connect('notion');
    } catch (error) {
      // Error is already set in the hook
      console.error('Failed to connect Notion:', error);
    }
  };

  const handleManage = async (provider) => {
    setLoadingManagement(true);
    setManageModal({ provider });

    try {
      const token = localStorage.getItem('token');

      if (provider === 'notion') {
        const res = await fetch(`${API_BASE_URL}/api/integrations/notion/pages`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setManagementData({ pages: data.pages || [], metadata: data.metadata });
        }
      } else if (provider === 'slack') {
        const res = await fetch(`${API_BASE_URL}/api/integrations/slack/channels`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setManagementData({ channels: data.channels || [], metadata: data.metadata });
        }
      } else if (provider === 'google' || provider === 'google_calendar') {
        // Placeholder for managing Google permissions if needed
        setManagementData({ scopes: ['email', 'calendar'], metadata: {} });
      }
    } catch (error) {
      console.error('Failed to fetch management data:', error);
    } finally {
      setLoadingManagement(false);
    }
  };

  const handleDisconnect = async (provider) => {
    if (!window.confirm(`Are you sure you want to disconnect ${provider}?`)) return;

    try {
      await disconnectIntegration(provider);
      
      setStatusMessage({ type: 'success', text: `${provider} disconnected successfully` });
      setTimeout(() => setStatusMessage(null), 3000);
      setManageModal(null);
      
      // Status is automatically refreshed by the hook's disconnect method
    } catch (error) {
      console.error('Failed to disconnect:', error);
      // Error message is already shown by the hook
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin opacity-50" />
      </div>
    );
  }

  return (
    <div className={`p-8 h-full overflow-y-auto custom-scrollbar ${darkMode ? 'text-white' : 'text-slate-900'}`}>
      {statusMessage && (
        <div className={`mb-6 p-4 rounded-xl ${statusMessage.type === 'success' ? 'bg-green-500/20 text-green-300' : 'bg-red-500/20 text-red-300'}`}>
          {statusMessage.text}
        </div>
      )}
      <h2 className="text-2xl font-black mb-6 flex items-center gap-3">
        <Calendar className="w-8 h-8" style={{ color: THEME.primary }} />
        Integrations
      </h2>
      <p className={`mb-8 max-w-2xl ${darkMode ? 'opacity-70' : 'text-slate-600'}`}>
        Connect HiCapy with your favorite tools to automatically sync meetings, export notes, and trigger workflows.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Gmail & Calendar Card (Main AI Assistant Connection) */}
        <div className={`p-6 rounded-3xl border transition-all ${status?.google?.connected
          ? darkMode ? 'bg-gradient-to-br from-blue-900/30 to-purple-900/30 border-blue-500/30' : 'bg-gradient-to-br from-blue-50 to-purple-50 border-blue-200'
          : darkMode ? 'bg-[#1F2833]/50 border-white/5' : 'bg-white border-orange-100 shadow-sm'
          }`}>
          <div className="flex justify-between items-start mb-4">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center shadow-md">
              <img src="https://www.gstatic.com/images/branding/product/1x/gmail_2020q4_48dp.png" alt="Gmail" className="w-8 h-8" />
            </div>
            {status?.google?.connected ? (
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium">Connected</span>
            ) : (
              <button
                onClick={handleGoogleConnect}
                className="px-3 py-1 rounded-full bg-secondary text-white text-xs font-medium hover:opacity-90 transition-opacity"
              >
                Connect
              </button>
            )}
          </div>
          <h3 className="font-medium text-lg mb-1">Gmail & Calendar</h3>
          <p className={`text-xs mb-2 ${darkMode ? 'text-secondary' : 'text-secondary'}`}>
            ⭐ Required for AI Assistant
          </p>
          <p className={`text-sm mb-4 ${darkMode ? 'opacity-60' : 'text-slate-500'}`}>
            {status?.google?.connected
              ? `Connected as ${status.google.metadata?.accountEmail || 'your account'}`
              : 'Let HiCapy read emails, draft replies, and manage your calendar.'}
          </p>
          {status?.google?.connected ? (
            <button
              className={`w-full py-3 rounded-xl font-medium text-sm border ${darkMode ? 'border-white/20 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`}
              onClick={() => handleManage('google')}
            >
              Manage
            </button>
          ) : (
            <div className={`p-3 rounded-xl text-xs ${darkMode ? 'bg-white/5' : 'bg-slate-50'}`}>
              <p className={darkMode ? 'text-gray-400' : 'text-slate-600'}>
                📧 Read & draft emails<br />
                📅 Access calendar events<br />
                🤖 AI actions require approval
              </p>
            </div>
          )}
        </div>

        {/* Google Calendar Card */}
        <div className={`p-6 rounded-3xl border transition-all ${darkMode ? 'bg-slate-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex justify-between items-start mb-4">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center border border-slate-100">
              <img src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Google_Calendar_icon_%282020%29.svg" alt="GCal" className="w-8 h-8" />
            </div>
            {status?.googleCalendar?.connected ? (
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium">Connected</span>
            ) : (
              <button
                onClick={handleGoogleCalendarConnect}
                className="px-3 py-1 rounded-full bg-secondary text-white text-xs font-medium hover:bg-secondary/90 transition-colors"
              >
                Connect
              </button>
            )}
          </div>
          <h3 className="font-medium text-lg mb-2">Google Calendar</h3>
          <p className={`text-sm mb-6 ${darkMode ? 'opacity-60' : 'text-slate-500'}`}>Syncs meetings and events automatically.</p>
          {status?.googleCalendar?.connected && (
            <button className={`w-full py-3 rounded-xl font-medium text-sm border ${darkMode ? 'border-white/20 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`}
              onClick={() => handleManage('google_calendar')}
            >
              Manage
            </button>
          )}
        </div>

        {/* Notion Card */}
        <div className={`p-6 rounded-3xl border transition-all ${darkMode ? 'bg-slate-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex justify-between items-start mb-4">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center border border-slate-100 overflow-hidden">
              {status?.notion?.workspaceIcon ? (
                <img src={status.notion.workspaceIcon} alt="Notion" className="w-full h-full object-cover" />
              ) : (
                <img src="https://upload.wikimedia.org/wikipedia/commons/4/45/Notion_app_logo.png" alt="Notion" className="w-8 h-8" />
              )}
            </div>
            {status?.notion?.connected ? (
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium">Connected</span>
            ) : (
              <button
                onClick={handleNotionConnect}
                className="px-3 py-1 rounded-full bg-secondary text-white text-xs font-medium hover:bg-secondary/90 transition-colors"
              >
                Connect
              </button>
            )}
          </div>
          <h3 className="font-medium text-lg mb-2">Notion</h3>
          <p className={`text-sm mb-6 ${darkMode ? 'opacity-60' : 'text-slate-500'}`}>
            {status?.notion?.connected
              ? `Connected to ${status.notion.workspaceName || 'Workspace'}`
              : 'Export meeting summaries to your workspace.'}
          </p>
          {status?.notion?.connected && (
            <button className={`w-full py-3 rounded-xl font-medium text-sm border ${darkMode ? 'border-white/20 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`}
              onClick={() => handleManage('notion')}
            >
              Manage
            </button>
          )}
        </div>

        {/* Slack Card */}
        <div className={`p-6 rounded-3xl border transition-all ${darkMode ? 'bg-slate-800/50 border-white/5' : 'bg-white border-slate-200 shadow-sm'}`}>
          <div className="flex justify-between items-start mb-4">
            <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center border border-slate-100">
              <img src="slack.png" alt="Slack" className="w-8 h-8" />
            </div>
            {status?.slack?.connected ? (
              <span className="px-3 py-1 rounded-full bg-emerald-500/10 text-emerald-500 text-xs font-medium">Connected</span>
            ) : (
              <button
                onClick={handleSlackConnect}
                className="px-3 py-1 rounded-full bg-secondary text-white text-xs font-medium hover:bg-secondary/90 transition-colors"
              >
                Connect
              </button>
            )}
          </div>
          <h3 className="font-medium text-lg mb-2">Slack</h3>
          <p className={`text-sm mb-6 ${darkMode ? 'opacity-60' : 'text-slate-500'}`}>
            {status?.slack?.connected
              ? `Connected to ${status.slack.teamName || 'Workspace'}`
              : 'Post meeting notes to your Slack channels.'}
          </p>
          {status?.slack?.connected && (
            <button className={`w-full py-3 rounded-xl font-medium text-sm border ${darkMode ? 'border-white/20 hover:bg-white/5' : 'border-slate-200 hover:bg-slate-50'}`}
              onClick={() => handleManage('slack')}
            >
              Manage
            </button>
          )}
        </div>
      </div>

      {/* Management Modal */}
      {manageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`max-w-2xl w-full max-h-[80vh] overflow-y-auto rounded-3xl p-6 ${darkMode ? 'bg-primary' : 'bg-white'}`}>
            <div className="flex justify-between items-start mb-6">
              <h3 className="text-2xl font-medium capitalize">{manageModal.provider} Integration</h3>
              <button onClick={() => setManageModal(null)} className={`p-2 rounded-xl ${darkMode ? 'hover:bg-white/5' : 'hover:bg-gray-100'}`}>
                <X size={20} />
              </button>
            </div>

            {loadingManagement ? (
              <div className="flex justify-center py-8">
                <Loader2 className="animate-spin" size={32} />
              </div>
            ) : (
              <>
                {/* Notion Pages */}
                {manageModal.provider === 'notion' && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Database size={16} />
                        Accessible Pages & Databases
                      </h4>
                      <p className={`text-sm mb-4 ${darkMode ? 'opacity-60' : 'text-slate-600'}`}>
                        You granted HiCapy access to {managementData.pages?.length || 0} page(s) and database(s).
                      </p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {managementData.pages?.map(page => (
                          <div key={page.id} className={`p-3 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{page.icon || (page.isDatabase ? '📊' : '📄')}</span>
                              <span className="font-medium">{page.title}</span>
                              {page.isDatabase && <span className="text-xs px-2 py-1 rounded bg-secondary/20 text-secondary">Database</span>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-white/10">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Lock size={16} />
                        Permissions
                      </h4>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'opacity-70' : 'text-slate-600'}`}>
                        <li>• Read content from selected pages</li>
                        <li>• Create new pages as sub-pages</li>
                        <li>• Update page properties and content</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* Slack Channels */}
                {manageModal.provider === 'slack' && (
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Hash size={16} />
                        Available Channels
                      </h4>
                      <p className={`text-sm mb-4 ${darkMode ? 'opacity-60' : 'text-slate-600'}`}>
                        {managementData.channels?.filter(ch => ch.isMember).length || 0} of {managementData.channels?.length || 0} accessible channels.
                      </p>
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {managementData.channels?.map(channel => (
                          <div key={channel.id} className={`p-3 rounded-xl ${darkMode ? 'bg-white/5' : 'bg-gray-50'}`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span className="font-medium">#{channel.name}</span>
                                {!channel.isMember && <span className="text-xs px-2 py-1 rounded bg-orange-500/20 text-orange-400">Not Joined</span>}
                              </div>
                              <div className="flex items-center gap-2">
                                {channel.isPrivate && <span className="text-xs px-2 py-1 rounded bg-yellow-500/20 text-yellow-400">Private</span>}
                                <span className={`text-xs ${darkMode ? 'opacity-50' : 'text-slate-500'}`}>{channel.memberCount} members</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="pt-4 border-t border-white/10">
                      <h4 className="font-medium mb-2 flex items-center gap-2">
                        <Lock size={16} />
                        Permissions
                      </h4>
                      <ul className={`text-sm space-y-1 ${darkMode ? 'opacity-70' : 'text-slate-600'}`}>
                        <li>• Post messages to channels</li>
                        <li>• Join public channels</li>
                        <li>• Read channel information</li>
                        <li>• Read private channels (groups)</li>
                        <li>• Read team information</li>
                      </ul>
                    </div>
                  </div>
                )}

                {/* Google / Calendar Fallback */}
                {(manageModal.provider === 'google' || manageModal.provider === 'google_calendar') && (
                  <div className="space-y-4">
                    <p className={`text-sm ${darkMode ? 'opacity-70' : 'text-slate-600'}`}>
                      Manage your Google connection settings from your Google Account portal to revoke access.
                    </p>
                  </div>
                )}

                {/* Disconnect Button */}
                <div className="pt-6 border-t border-white/10 mt-6">
                  <button
                    onClick={() => handleDisconnect(manageModal.provider)}
                    className="w-full py-3 rounded-xl font-medium bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors"
                  >
                    Disconnect {manageModal.provider.charAt(0).toUpperCase() + manageModal.provider.slice(1)}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Integrations;